import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

import type { CalendarEvent, ISODate } from '../../shared/ui/components/EventCalendar/types';
import { addDays } from '../../shared/ui/components/EventCalendar/utils';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { adminCalendarViewApi, useLazyGetAdminCalendarViewQuery } from './api/adminCalendarViewApi';
import { toCalendarEvents } from './calendarViewTransforms';
import type { AdminCalendarViewResponse } from './types';

// The admin calendar API caps each request at 42 days, so the List view loads
// in 42-day pages and accumulates them as the user scrolls.
const PAGE_DAYS = 42;
// Small past buffer so the list opens with a little history above today.
const INITIAL_PAST_DAYS = 7;

type Warning = AdminCalendarViewResponse['warnings'][number];

interface Params {
  personId: string;
  anchor: ISODate;
  enabled: boolean;
}

export interface IncrementalCalendarEvents {
  events: CalendarEvent[];
  warnings: Warning[];
  isLoadingInitial: boolean;
  isLoadingFuture: boolean;
  isLoadingPast: boolean;
  error: unknown;
  /** Set when an incremental page load fails, so the edge can show a retry. */
  pageError: 'past' | 'future' | null;
  loadFuture: () => void;
  loadPast: () => void;
}

function warningKey(warning: Warning): string {
  return [warning.code, warning.connection_id, warning.provider_calendar_id]
    .filter(Boolean)
    .join(':');
}

function mergeWarnings(first: Warning[], second: Warning[]): Warning[] {
  const seen = new Set(first.map(warningKey));
  const merged = [...first];
  for (const warning of second) {
    const key = warningKey(warning);
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(warning);
    }
  }
  return merged;
}

function mergeById(first: CalendarEvent[], second: CalendarEvent[]): CalendarEvent[] {
  const seen = new Set(first.map((event) => event.id));
  const merged = [...first];
  for (const event of second) {
    if (!seen.has(event.id)) {
      seen.add(event.id);
      merged.push(event);
    }
  }
  return merged;
}

function upsertPage(
  pages: AdminCalendarViewResponse[],
  incoming: AdminCalendarViewResponse
): AdminCalendarViewResponse[] {
  const matchingIndex = pages.findIndex(
    (page) => page.range.start === incoming.range.start && page.range.end === incoming.range.end
  );
  if (matchingIndex === -1) return [...pages, incoming];
  return pages.map((page, index) => (index === matchingIndex ? incoming : page));
}

export function useIncrementalCalendarEvents({
  personId,
  anchor,
  enabled,
}: Params): IncrementalCalendarEvents {
  const dispatch = useAppDispatch();
  const [trigger, { reset: resetTrigger }] = useLazyGetAdminCalendarViewQuery();
  const [pages, setPages] = useState<AdminCalendarViewResponse[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingFuture, setIsLoadingFuture] = useState(false);
  const [isLoadingPast, setIsLoadingPast] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [pageError, setPageError] = useState<'past' | 'future' | null>(null);

  // Loaded contiguous window [start, end). Refs keep load callbacks stable.
  const loadedStartRef = useRef<ISODate | null>(null);
  const loadedEndRef = useRef<ISODate | null>(null);
  const futureInFlight = useRef(false);
  const pastInFlight = useRef(false);
  const retainedSubscriptionsRef = useRef(new Map<string, { unsubscribe: () => void }>());
  // Bumped on every reset so stale in-flight pages are ignored.
  const generationRef = useRef(0);
  // Identifies which coach/anchor the current pages belong to. Used to avoid
  // rendering a previous coach's events for a paint before the reset effect runs.
  const requestKey = `${personId}|${anchor}`;
  const loadedKeyRef = useRef<string | null>(null);

  const fetchPage = useCallback(
    (start: ISODate, end: ISODate) => {
      const request = { personId, start, end };
      const result = trigger(request, true);
      const subscriptionKey = `${personId}|${start}|${end}`;
      if (!retainedSubscriptionsRef.current.has(subscriptionKey)) {
        retainedSubscriptionsRef.current.set(
          subscriptionKey,
          dispatch(adminCalendarViewApi.endpoints.getAdminCalendarView.initiate(request))
        );
      }
      return result.unwrap();
    },
    [dispatch, trigger, personId]
  );

  const cachedPages = useAppSelector(
    (state) =>
      pages
        .map((page) =>
          adminCalendarViewApi.endpoints.getAdminCalendarView.select({
            personId,
            start: page.range.start,
            end: page.range.end,
          })(state)
        )
        .map((result) => result.data)
        .filter((page): page is AdminCalendarViewResponse => page !== undefined),
    shallowEqual
  );

  useEffect(() => {
    if (cachedPages.length === 0) return;
    setPages((current) => cachedPages.reduce(upsertPage, current));
  }, [cachedPages]);

  // Reset and load the initial window whenever the coach or anchor changes.
  useEffect(() => {
    // Bump the generation on every change — including disabling List view — so
    // any in-flight page load is invalidated and can't merge after we've moved
    // on (left List, or changed coach while another view is active).
    const generation = ++generationRef.current;
    resetTrigger();
    for (const subscription of retainedSubscriptionsRef.current.values())
      subscription.unsubscribe();
    retainedSubscriptionsRef.current.clear();
    futureInFlight.current = false;
    pastInFlight.current = false;
    // Clear stale data up front so a previous coach/range never lingers and
    // showInitialLoading reflects the fresh fetch. Also clear the edge spinners:
    // an in-flight page load's gen-guarded finally won't clear them post-reset.
    loadedStartRef.current = null;
    loadedEndRef.current = null;
    loadedKeyRef.current = null;
    setPages([]);
    setError(null);
    setPageError(null);
    setIsLoadingInitial(false);
    setIsLoadingFuture(false);
    setIsLoadingPast(false);
    if (!enabled) return;
    const start = addDays(anchor, -INITIAL_PAST_DAYS);
    const end = addDays(anchor, PAGE_DAYS - INITIAL_PAST_DAYS);
    loadedKeyRef.current = requestKey;
    setIsLoadingInitial(true);
    fetchPage(start, end)
      .then((res) => {
        if (generationRef.current !== generation) return;
        setPages([res]);
        loadedStartRef.current = start;
        loadedEndRef.current = end;
        loadedKeyRef.current = requestKey;
      })
      .catch((err) => {
        if (generationRef.current === generation) setError(err);
      })
      .finally(() => {
        if (generationRef.current === generation) setIsLoadingInitial(false);
      });
    return () => {
      resetTrigger();
      for (const subscription of retainedSubscriptionsRef.current.values()) {
        subscription.unsubscribe();
      }
      retainedSubscriptionsRef.current.clear();
    };
  }, [enabled, anchor, fetchPage, requestKey, resetTrigger]);

  const loadFuture = useCallback(() => {
    const from = loadedEndRef.current;
    if (!from || futureInFlight.current) return;
    const generation = generationRef.current;
    futureInFlight.current = true;
    setIsLoadingFuture(true);
    setPageError(null);
    fetchPage(from, addDays(from, PAGE_DAYS))
      .then((res) => {
        if (generationRef.current !== generation) return;
        setPages((current) => upsertPage(current, res));
        loadedEndRef.current = addDays(from, PAGE_DAYS);
      })
      .catch(() => {
        if (generationRef.current === generation) setPageError('future');
      })
      .finally(() => {
        // Only clear guards for the current generation; a stale fetch resolving
        // after a reset must not unlock the new generation's in-flight guard.
        if (generationRef.current === generation) {
          futureInFlight.current = false;
          setIsLoadingFuture(false);
        }
      });
  }, [fetchPage]);

  const loadPast = useCallback(() => {
    const to = loadedStartRef.current;
    if (!to || pastInFlight.current) return;
    const generation = generationRef.current;
    pastInFlight.current = true;
    setIsLoadingPast(true);
    setPageError(null);
    fetchPage(addDays(to, -PAGE_DAYS), to)
      .then((res) => {
        if (generationRef.current !== generation) return;
        setPages((current) => upsertPage(current, res));
        loadedStartRef.current = addDays(to, -PAGE_DAYS);
      })
      .catch(() => {
        if (generationRef.current === generation) setPageError('past');
      })
      .finally(() => {
        if (generationRef.current === generation) {
          pastInFlight.current = false;
          setIsLoadingPast(false);
        }
      });
  }, [fetchPage]);

  // `events` are only valid for the coach/anchor they were loaded for. Until the
  // reset effect runs and the fresh page resolves, treat mismatched events as
  // "still loading" so a previous coach's data never renders for a paint.
  const isStale = enabled && loadedKeyRef.current !== requestKey;
  const events = pages.reduce(
    (mergedEvents, page) => mergeById(mergedEvents, toCalendarEvents(page)),
    [] as CalendarEvent[]
  );
  const warnings = pages.reduce(
    (mergedWarnings, page) => mergeWarnings(mergedWarnings, page.warnings),
    [] as Warning[]
  );

  return {
    events: isStale ? [] : events,
    warnings: isStale ? [] : warnings,
    isLoadingInitial: enabled && (isLoadingInitial || isStale),
    isLoadingFuture: isStale ? false : isLoadingFuture,
    isLoadingPast: isStale ? false : isLoadingPast,
    error: isStale ? null : error,
    pageError: isStale ? null : pageError,
    loadFuture,
    loadPast,
  };
}
